import * as React from 'react'
import Toggle from 'src/components/ui/Toggle'
import { IPaymentMethods, IServicePricingDiscount } from 'src/types'
import { IServicePricingDiscountDTO, IServicePricingModel } from '../../../types/service-pricing'
import { parseDiscountLabel } from 'src/utils'
import { upperFirst } from 'lodash'

interface IServiceViewPricingBreakdownProps {
  serviceId: number
  orgId: number
  pricing: IServicePricingModel
  paymentMethods: IPaymentMethods[]
  servicePricingDiscount: IServicePricingDiscount
  onComponentMount: (serviceId: number, orgId: number) => void
}

interface IServiceViewPricingBreakdownState {
  pricingDiscounts: IServicePricingDiscountDTO[]
  showLess: boolean
}

class ServiceViewPricingBreakdown extends React.Component<
  IServiceViewPricingBreakdownProps,
  IServiceViewPricingBreakdownState
> {
  constructor(props: IServiceViewPricingBreakdownProps) {
    super(props)
    this.state = {
      pricingDiscounts: [],
      showLess: false
    }
  }

  public componentDidMount() {
    const { serviceId, orgId, onComponentMount } = this.props
    onComponentMount(serviceId, orgId)
  }

  public componentDidUpdate(
    prevProps: IServiceViewPricingBreakdownProps,
    prevState: IServiceViewPricingBreakdownState,
    snapshot?: any
  ): void {
    const { data: pricingDiscounts } = this.props.servicePricingDiscount
    const { data: prevPricingDiscounts } = prevProps.servicePricingDiscount
    if (pricingDiscounts !== prevPricingDiscounts) {
      const sortedData = [...pricingDiscounts].sort((a, b) => a.componentName.localeCompare(b.componentName))
      this.setState({
        pricingDiscounts: sortedData.length > 8 ? sortedData.slice(0, 8) : sortedData,
        showLess: sortedData.length > 8
      })
    }
  }

  public render() {
    const { pricingDiscounts, showLess } = this.state
    const { pricing, paymentMethods, servicePricingDiscount } = this.props
    const isManualCollectEnabled = pricing ? pricing.isManualCollectEnabled : false
    const fare = pricing ? pricing.fare / 100 : null
    const promoFare = pricing ? pricing.promoFare / 100 : null
    const promoLimit = pricing ? pricing.promoLimit : 0
    const promoDuration = pricing ? pricing.promoDuration : ''
    const commissionFlat = pricing ? pricing.commissionFlat / 100 : 0
    const commission = pricing ? pricing.commissionPercentage / 100 : 0
    const discountFlat = pricing ? pricing.discountFlat / 100 : 0
    const discount = pricing ? pricing.discountPercentage / 100 : 0
    const stateTax = pricing ? pricing.stateTax / 100 : 0
    const federalTax = pricing ? pricing.federalTax / 100 : 0
    const freeAttendant = false
    const isRemoveCentsEnabled = pricing ? pricing.isRemoveCentsEnabled : false

    const PaymentMethodItem = ({ paymentName, activated }: IPaymentMethods) => (
      <div className='flex flex-row justify-start items-center mt2'>
        <div className='col-3 flex flex-column h5 font-dark-grey mr2'>{paymentName}</div>
        <div className='col-2 flex flex-column h5 font-green bold'>
          <Toggle state={activated} onClick={() => null} />
        </div>
      </div>
    )

    const handleDiscountSlicing = () => {
      if (servicePricingDiscount.data.length > 8) {
        this.setState({ showLess: !showLess }, () => {
          showLess
            ? this.setState({
                pricingDiscounts: [...servicePricingDiscount.data].sort((a, b) =>
                  a.componentName.localeCompare(b.componentName)
                )
              })
            : this.setState({
                pricingDiscounts: [...servicePricingDiscount.data]
                  .sort((a, b) => a.componentName.localeCompare(b.componentName))
                  .slice(0, 8)
              })
        })
      }
    }

    return (
      <div className='border-box px2 col-12'>
        <div>
          <div className='h4 mt3 bold font-dark-grey'>Payment Types</div>
          {paymentMethods.map((paymentMethod, i) => (
            <PaymentMethodItem {...paymentMethod} key={`${i}-${paymentMethod.paymentName}`} />
          ))}
        </div>
        <div className='h4 mt3 bold font-dark-grey'>Price modifier</div>

        <div className='h6 font-grey3 mt3'>Manual Fare Collection</div>
        <div className='mt1 h5 font-dark-grey col-4'>{isManualCollectEnabled ? 'Yes' : 'No'}</div>

        <div className='mt2'>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5 font-dark-grey'>Fare</label>
            <label className='mt1'>{!!fare ? `$${fare}` : '0'}</label>
          </div>
          {promoLimit > 0 ? (
            [
              <div key={1} className='h4 mt3 bold font-dark-grey'>
                Promotional Fare (This fare is applied first)
              </div>,
              <div key={2} className='flex flex-row mt2'>
                <div className='col-4 flex flex-column pr2'>
                  <label className='h5 font-dark-grey'>Fare</label>
                  <label className='mt1'>{!!promoFare ? `$${promoFare}` : '0'}</label>
                </div>
                <div className='col-4 flex flex-column pr2'>
                  <label className='h5 font-dark-grey'>Number completed</label>
                  <label className='mt1'>{promoLimit}</label>
                </div>
                <div className='col-4 flex flex-column pr2'>
                  <label className='h5 font-dark-grey'>Per</label>
                  <label className='mt1'>{promoDuration}</label>
                </div>
                <div className='col-4 pr2' />
              </div>
            ]
          ) : (
            <div className='h4 mt3 bold font-dark-grey'>No promotional fare applied.</div>
          )}
        </div>
        <div className='h4 mt3 bold font-dark-grey'>Total Pricing Discount</div>
        <div className='flex flex-row mt2'>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5 font-dark-grey'>Discount</label>
            <label className='mt1'>{!!discount ? `${discount} %` : '0'}</label>
          </div>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5 font-dark-grey'>Discount flat</label>
            <label className='mt1'>{!!discountFlat ? `$${discountFlat}` : '0'}</label>
          </div>
          <div className='col-4 pr2' />
          <div className='col-4 pr2' />
        </div>
        {!!pricingDiscounts.length && (
          <>
            <div className='h4 mt3 bold font-dark-grey'>Individual Pricing Discount</div>
            <div className='col-12 pt2'>
              <div className='mbsc-grid no-padding'>
                <div className='mbsc-row'>
                  {pricingDiscounts.map(({ componentName, discountType, discountValue, id, isEnabled }) => (
                    <div className='mbsc-col-12 mbsc-col-md-4 mbsc-col-lg-3 pr2 py2' key={id}>
                      <div className='flex flex-column row-gap-8'>
                        <div className='flex flex-column row-gap-8'>
                          <label className='bold h5' id={`${id}-component-label`}>
                            {parseDiscountLabel(componentName)}
                          </label>
                          <label className='h5' id={`${id}-value-label`}>
                            {discountValue}
                          </label>
                        </div>
                        <label className='h5' id={`${id}-type-label`}>{`Discount Type: ${upperFirst(
                          discountType
                        )}`}</label>
                        <label className='h5' id={`${id}-enabled-label`}>{`Enabled: ${
                          isEnabled ? 'Yes' : 'No'
                        }`}</label>
                      </div>
                    </div>
                  ))}
                  {servicePricingDiscount.data.length > 8 && (
                    <div className='flex justify-end mbsc-col-12'>
                      <label className='bold cursor--pointer h5 italic underline' onClick={handleDiscountSlicing}>
                        {showLess ? 'See all' : 'See less'}
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        <div className='h4 mt3 bold font-dark-grey'>Increase</div>
        <div className='flex flex-row mt2'>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5 font-dark-grey'>Commission Flat</label>
            <label className='mt1'>{!!commissionFlat ? `$${commissionFlat}` : '0'}</label>
          </div>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5 font-dark-grey'>Commission</label>
            <label className='mt1'>{!!commission ? `${commission} %` : '0'}</label>
          </div>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5 font-dark-grey'>State tax</label>
            <label className='mt1'>{!!stateTax ? `${stateTax} %` : '0'}</label>
          </div>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5 font-dark-grey'>Federal tax</label>
            <label className='mt1'>{!!federalTax ? `${federalTax} %` : '0'}</label>
          </div>
        </div>
        <div className='h6 font-grey3 mt3'>Remove cents in product pricing</div>
        <div className='mt1 h5 font-dark-grey col-4'>{isRemoveCentsEnabled ? 'Yes' : 'No'}</div>
      </div>
    )
  }
}

export default ServiceViewPricingBreakdown



// WEBPACK FOOTER //
// ./src/components/services/view/ServiceViewPricingBreakdown.tsx